import React from 'react';
import { Box, Divider, SelectChangeEvent, Stack } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { FormMessage, ScheduledType } from '../../types';
import { DelayInput, DelayInputProps, useDelayField } from './DelayInput';
import { ScheduleOption, ScheduleTypeSelect } from './ScheduleTypeSelect';
import {
  MessageScheduleDateTimePicker,
  messageSchedulePickerSx,
  messageScheduleRowSx,
  messageSchedulePickerDemoContainerSx,
  messageScheduleTimePickerContainerSx,
} from './MessageScheduleDateTimePicker';

/** Which absolute-time control a kind offers, once "time" is chosen. */
export type TimeControl = 'none' | 'dateTime' | 'timeOfDay';

export interface ScheduleRowProps {
  message: FormMessage;
  onChange: (next: Partial<FormMessage>) => void;
  disabled?: boolean;
  /** Lower bound for the absolute-time picker. */
  minDateTime?: Dayjs | null;
  scheduleTimeError?: string;

  options: ScheduleOption[];
  /** Unit the delay editor starts on. Reminders count in days. */
  delayPeriod: string;
  /** Adopt the unit of an already-saved delay on mount. The flyer never did. */
  syncPeriodOnMount?: boolean;
  /** Reminder widens and captions the delay editor; join re-centres it. */
  delayInput?: Partial<DelayInputProps>;
  timeControl?: TimeControl;
  dividerSx?: SxProps<Theme>;
  /** Reminder packs its row tighter than the rest. */
  compact?: boolean;
  /** Enrollment reserves a helper row below its required follow-up time. */
  showTimeHelperText?: boolean;
}

const toIso = (value: Dayjs | null) =>
  (value && value.isValid() ? value.toDate().toISOString() : null);

/**
 * One schedule row: the "Schedule:" dropdown plus whichever editor the chosen
 * type needs. Every campaign kind draws this same row and differs only in the
 * props above — the four hand-written variants were 90% identical.
 */
export const ScheduleRow: React.FC<ScheduleRowProps> = ({
  message, onChange, disabled, minDateTime, scheduleTimeError,
  options, delayPeriod, syncPeriodOnMount = true, delayInput,
  timeControl = 'none', dividerSx, compact, showTimeHelperText = false,
}) => {
  const delay = useDelayField({ message, onChange, defaultPeriod: delayPeriod, syncPeriodOnMount });

  const handleTypeChange = ({ target }: SelectChangeEvent<string>) => {
    const value = target.value as ScheduledType;
    // A delay is about to be typed, so only the absolute time is dropped.
    if (value === 'delay') {
      onChange({ scheduledType: 'delay', time: null });
      return;
    }
    delay.clear();
    onChange(value === 'time'
      ? { scheduledType: 'time', delay: null }
      : { scheduledType: value, time: null, delay: null });
  };

  // A saved type this position no longer offers leaves the dropdown blank rather
  // than showing a value the user could not pick again.
  const selected = options.some(o => o.value === message.scheduledType)
    ? message.scheduledType
    : '';

  const pickerTime = message.time ? dayjs(message.time) : null;

  return (
    <>
      <Divider sx={dividerSx} />
      <Stack
        direction="row"
        alignItems={compact ? 'center' : 'flex-start'}
        height={compact ? '30px' : undefined}
        sx={compact ? { marginTop: '10px', marginBottom: '10px' } : messageScheduleRowSx}
      >
        <ScheduleTypeSelect
          value={selected}
          options={options}
          onChange={handleTypeChange}
          disabled={disabled}
        />

        {message.scheduledType === 'delay' && (
          <DelayInput
            amount={delay.amount}
            period={delay.period}
            onAmountChange={delay.handleAmountChange}
            onPeriodChange={delay.handlePeriodChange}
            disabled={disabled}
            {...delayInput}
          />
        )}

        {message.scheduledType === 'time' && timeControl === 'dateTime' && (
          <MessageScheduleDateTimePicker
            disabled={disabled}
            hasError={!!scheduleTimeError}
            helperText={scheduleTimeError || (showTimeHelperText ? ' ' : undefined)}
            minDateTime={minDateTime?.isValid() ? minDateTime : null}
            onChange={value => onChange({ time: toIso(value) })}
            value={pickerTime}
          />
        )}

        {message.scheduledType === 'time' && timeControl === 'timeOfDay' && (
          // A flyer schedules a time of day, not a full date, and has no lower
          // bound beyond "not in the past".
          <Box sx={messageScheduleTimePickerContainerSx}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {/* 'DateTimePicker' sizes DemoContainer's child TextField at 270px;
                  'TimePicker' would drop it to 200. The child is a TimePicker
                  either way — this name only picks the width. */}
              <DemoContainer components={['DateTimePicker']} sx={messageSchedulePickerDemoContainerSx}>
                <TimePicker
                  sx={messageSchedulePickerSx}
                  slotProps={{ textField: scheduleTimeError ? { error: true } : {} }}
                  ampm
                  format="hh:mm A"
                  disablePast
                  closeOnSelect={false}
                  disabled={disabled}
                  onChange={value => onChange({ delay: null, time: toIso(value) })}
                  defaultValue={pickerTime}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Box>
        )}
      </Stack>
    </>
  );
};
